import { useQuery } from "@tanstack/react-query";
import {
  FLEET_STALE_AFTER_SECONDS,
  estadoDeLeitura,
  fonteNaoFornece,
  leituraDeIdade,
  temLeituraCorrente,
  type CcoOverviewResponse,
} from "@nxvolt/shared";
import {
  blocoAusenteEsperaDado,
  fraseDoBlocoAusente,
  frasesDeAviso,
} from "./cco-avisos.js";
import PatioMatrix from "../components/PatioMatrix.js";
import { Skeleton } from "../components/Skeleton.js";
import { fmtAge } from "../components/substation/bands.js";
import {
  causaDoTempoRestante,
  configuracaoDeFrota,
  estadoDeValor,
  FRASE_DO_CAMPO,
  FRASE_DO_TEMPO_RESTANTE,
  fraseDeOnibusVisiveis,
  fraseDeRecorteDaTabela,
  valorDeParcela,
  ressalvaDeTruncamento,
  ROTULO_DO_CAMPO,
  TRATAMENTO_DA_LEITURA,
  type EstadoDoCampo,
  TRATAMENTO_DO_CAMPO,
  idadeFormatadaDaLeitura,
  textoDaIdadeDaLeitura,
  type ConfiguracaoDeFrota,
} from "../components/fleet/aggregates.js";
import {
  ESTILO_DEGRADADO_GRAFICO,
  ESTILO_DEGRADADO_TEXTO,
  SufixoDeRessalva,
} from "../components/fleet/FleetCells.js";

/**
 * Quantas linhas da frota a tabela do CCO mostra.
 *
 * Constante nomeada porque o numero estava escrito DUAS vezes — no `slice` e na
 * frase que declara o recorte. Duas redacoes do mesmo numero divergem.
 */
const LIMITE_DA_TABELA = 12;
import {
  IconBattery,
  IconBell,
  IconBolt,
  IconTower,
  IconTrend,
  IconTruck,
  IconWrench,
} from "../components/icons.js";
import "../styles/cco.css";
import "../styles/patio-charger.css";

type CcoFrota = NonNullable<CcoOverviewResponse["frota"]>;
type CcoEnergia = NonNullable<CcoOverviewResponse["energia"]>;
type CcoOcpp = NonNullable<CcoOverviewResponse["ocpp"]>;
type CcoRelatorios = NonNullable<CcoOverviewResponse["relatorios"]>;
type PatioStatus = CcoOcpp["layoutPatio"][number]["status"];
type CcoVeiculo = CcoFrota["telemetria"][number];

/**
 * A linha do quadrante de frota, adaptada à forma que os primitivos de
 * `frescor.ts` esperam.
 *
 * `leituraDeIdade` é a FRONTEIRA: `ageSeconds: null` vira `latest: null`, nunca
 * `ageSeconds: 0`. Sem ela o quadrante intitulado "Estado atual dos veículos com
 * sinal" contaria `COUNT(*)` sem filtro de frescor — o título afirmando um
 * recorte que o número não tem, e um veículo sem sinal há 28 dias entrando em
 * "carregando agora".
 */
function comLeitura(v: CcoVeiculo) {
  return { latest: leituraDeIdade(v.ageSeconds) };
}


async function fetchCcoOverview(): Promise<CcoOverviewResponse> {
  const response = await fetch("/api/cco/overview", { credentials: "include" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as CcoOverviewResponse;
}

function formatUpdated(iso: string | undefined): string {
  if (!iso) return "Atualizando dados";
  return `Atualizado às ${new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

function formatMinutes(value: number): string {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}` : `${minutes} min`;
}

function statusCounts(layout: CcoOcpp["layoutPatio"] | undefined): Record<PatioStatus, number> {
  const counts: Record<PatioStatus, number> = { available: 0, charging: 0, alert: 0, offline: 0 };
  for (const item of layout ?? []) counts[item.status] += 1;
  return counts;
}

function StateDot({ tone = "live" }: { tone?: "live" | "warning" | "muted" }) {
  return <span className={`nx-cco-state-dot nx-cco-state-dot--${tone}`} aria-hidden="true" />;
}

function OperationalStat({ label, value, detail, tone, icon }: {
  label: string;
  /**
   * `null` imprime TRAVESSAO, e e o que `valorDeParcela` devolve quando a
   * topologia e vazia. Sem esta possibilidade no tipo, a tela nao tinha como
   * dizer "nao ha frota" e so sabia dizer "zero" — dois zeros diferentes com
   * uma grafia so.
   */
  value: number | string | null;
  detail: string;
  tone: "navy" | "info" | "success" | "warning";
  icon: JSX.Element;
}) {
  return (
    <article className={`nx-cco-stat nx-cco-stat--${tone}`}>
      <span className="nx-cco-stat__icon">{icon}</span>
      <div>
        <span className="nx-cco-stat__label">{label}</span>
        <strong className="nx-cco-stat__value mono">
          {value ?? <span aria-hidden="true">—</span>}
        </strong>
        <span className="nx-cco-stat__detail">{detail}</span>
      </div>
    </article>
  );
}

function PanelHeader({ id, title, subtitle, href, action }: {
  id: string;
  title: string;
  subtitle?: string;
  href?: string;
  action?: string;
}) {
  return (
    <header className="nx-cco-panel__head">
      <div>
        <h2 id={id}>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {href && action ? <a href={href}>{action}</a> : null}
    </header>
  );
}

export function CcoPage() {
  const { data, error, isLoading } = useQuery<CcoOverviewResponse, Error>({
    queryKey: ["cco", "overview"],
    queryFn: fetchCcoOverview,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const counts = statusCounts(data?.ocpp?.layoutPatio);
  /* O vocabulário dos avisos — frases, e não tokens internos. Ver `cco-avisos.ts`. */
  const avisos = frasesDeAviso(data?.warnings ?? []);
  /* A ausência do bloco de frota ESPERA dado? `false` quando é impossibilidade
     de recorte, e aí a área ganha frase em vez de esqueleto. */
  const frotaEsperaDado = blocoAusenteEsperaDado('frota', data?.warnings ?? []);
  const fraseDaFrotaAusente =
    fraseDoBlocoAusente('frota', data?.warnings ?? []) ??
    'Os indicadores de frota não vieram nesta atualização.';
  /**
   * O limiar de frescor. `FLEET_STALE_AFTER_SECONDS` é o limiar ÚNICO da frota —
   * havia quatro, e três deles DESCARTAVAM o veículo vencido antes de contá-lo.
   *
   * O envelope do CCO ainda não publica `configuration.staleAfterSeconds`; está
   * pedido ao backend. Enquanto isso a constante compartilhada garante que esta
   * tela classifique a mesma frota do mesmo jeito que as outras cinco, que é a
   * propriedade que importa.
   */
  const cfg: ConfiguracaoDeFrota = configuracaoDeFrota(FLEET_STALE_AFTER_SECONDS);

  return (
    <div className="nx-cco">
      <header className="nx-cco-hero">
        <div>
          <span className="nx-page-head__eyebrow">Operação em tempo real</span>
          <h1>Centro de Controle Operacional</h1>
          <p>Frota, pátio de recarga e exceções em uma única visão.</p>
        </div>
        <div className="nx-cco-hero__status" role="status">
          <StateDot tone={error ? "warning" : "live"} />
          <span>{error ? "Falha na atualização" : formatUpdated(data?.generatedAt)}</span>
        </div>
      </header>

      {/*
        OS AVISOS DEIXAM DE SER TOKENS INTERNOS.

        Era `data.warnings.join(" · ")` — `frota_fora_de_escopo` lido pelo
        operador. Enquanto todo aviso era de FALHA isso era feio e transitório;
        `frota_fora_de_escopo` é o PRIMEIRO permanente, e o token passava a
        morar na tela para sempre. Ver `cco-avisos.ts`.
      */}
      {avisos.length > 0 ? (
        <section className="nx-cco-notice" role="status">
          <IconBell />
          <span>{avisos.join(" · ")}</span>
        </section>
      ) : null}
      {error && !data ? <section className="nx-cco-error" role="alert">Não foi possível carregar o CCO. {error.message}</section> : null}

      {/*
        OS KPIs DE FROTA. "Frota total" passou a declarar o recorte que tem.
        Antes `kpis.total` era `COUNT(*)` sem filtro de frescor debaixo de um
        quadrante intitulado "Estado atual dos veículos COM SINAL": o título
        afirmava um recorte que o número não tinha. Agora a contagem com sinal é
        derivada das linhas — o mesmo array que a tabela renderiza — e o total
        visível é declarado como total visível.
      */}
      <section className="nx-cco-stats" aria-label="Resumo operacional">
        {/*
          ESQUELETO SIGNIFICA "ESPERE", E HÁ UM CASO EM QUE ESPERAR NÃO RESOLVE.

          Era `isLoading || !data?.frota` -> quatro esqueletos. Com o backend
          trocando o 403 por filtragem, conta restrita passou a receber **200**
          com `frota: null` e `frota_fora_de_escopo` — e a tela pulsava quatro
          esqueletos de KPI PARA SEMPRE sobre o estado permanente e correto
          daquela conta. O servidor diz impossibilidade de recorte e a tela
          dizia aguarde: a fusão que ele acabou de banir do lado dele.

          `blocoAusenteEsperaDado` separa os dois: falha continua com esqueleto
          (transitória, auto-corrigível), impossibilidade ganha frase.
        */}
        {!isLoading && !data?.frota && !frotaEsperaDado ? (
          <p className="nx-cco-stats__fora-de-escopo" style={{ gridColumn: "1 / -1", margin: 0, color: "var(--nx-mute)" }}>
            {fraseDaFrotaAusente}
          </p>
        ) : isLoading || !data?.frota ? Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} variant="kpi" />) : <>
          <OperationalStat label="Ônibus visíveis" value={data.frota.totalVisivel} detail="topologia da fonte de telemetria ativa" tone="navy" icon={<IconTruck />} />
          {/* `valorDeParcela`: "COM SINAL 0" sobre topologia VAZIA afirma algo
              sobre onibus que nao existem. E o mesmo defeito ja corrigido duas
              vezes na Telemetria e no Mapa — aqui era a terceira copia, com
              `String(...)` cru. Ver `valorDeParcela`. */}
          <OperationalStat
            label="Com sinal"
            value={valorDeParcela(
              data.frota.telemetria.filter((v) => temLeituraCorrente(comLeitura(v), cfg)).length,
              data.frota.totalVisivel,
            )}
            detail={`leitura nos últimos ${fmtAge(cfg.staleAfterSeconds)}${
              data.frota.amostrados < data.frota.totalVisivel
                ? ` · entre os ${data.frota.amostrados} amostrados`
                : ""
            }`}
            tone="success"
            icon={<IconBolt />}
          />
          <OperationalStat label="Concluídas" value={valorDeParcela(data.frota.kpis.concluida24h, data.frota.totalVisivel)} detail="sessões nas últimas 24h" tone="info" icon={<IconBattery />} />
          <OperationalStat label="Manutenção" value={valorDeParcela(data.frota.kpis.manutencao, data.frota.totalVisivel)} detail="ordens abertas ou em curso" tone="warning" icon={<IconWrench />} />
        </>}
      </section>

      <section className="nx-cco-panel nx-cco-panel--fleet" aria-labelledby="cco-fleet-title">
        {/*
          O SUBTÍTULO declara o que a tabela mostra: TODOS os visíveis, com o
          estado de cada leitura. Dizia "Estado atual dos veículos com sinal" e
          listava também os sem sinal, apresentando snapshot velho como estado
          atual.
        */}
        <PanelHeader id="cco-fleet-title" title="Telemetria da frota" subtitle="Um ônibus por linha, com o estado da leitura de cada um" href="/app/telemetria_frota" action="Ver telemetria" />
        {!isLoading && !data?.frota && !frotaEsperaDado ? (
          <p style={{ color: "var(--nx-mute)" }}>{fraseDaFrotaAusente}</p>
        ) : isLoading || !data?.frota ? <Skeleton variant="row" count={6} /> : <>
          {(() => {
            const frota = data.frota;
            const truncamento = ressalvaDeTruncamento(frota.amostrados, frota.totalVisivel);
            /* A coluna de potência SOME quando nenhuma fonte exibida a fornece —
               mesma regra da tabela de Dados dos Ônibus. Uma coluna inteira de
               travessões ensina o operador a ignorar travessão. */
            const potenciaOculta =
              frota.telemetria.length > 0 &&
              frota.telemetria.every((v) =>
                fonteNaoFornece(v.unsupportedFields, "charging_power_kw"),
              );
            const fontes = [...new Set(frota.telemetria.map((v) => v.sourceLabel))].sort();
            return (
              <>
                {potenciaOculta && (
                  <p className="nx-cco-source-note" role="note">
                    <IconTower /> A coluna de {ROTULO_DO_CAMPO.charging_power_kw.nome} foi omitida:{" "}
                    {fontes.join(" e ")} não fornece esse dado para nenhum ônibus visível, e
                    por isso a previsão de término também não é calculável.
                  </p>
                )}
                <div className="nx-cco-table-wrap">
                  <table className="nx-cco-table">
                    {/*
                      O NOME ACESSIVEL E O `scope`, que a tabela vizinha ja tinha.

                      Esta tabela tinha 0 de 6 `scope`, nenhum `<caption>` e
                      nenhum `aria-label`: o leitor de tela anunciava "tabela, 6
                      colunas" e as celulas nao se associavam a coluna nenhuma —
                      uma lista de numeros soltos. Sao os mesmos tres consertos
                      feitos na Telemetria, aplicados onde nao tinham sido.
                    */}
                    <caption className="nx-sr-only">
                      {`Estado de carga por ônibus. ${fraseDeOnibusVisiveis(frota.totalVisivel)}.`}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Ônibus</th>
                        <th scope="col">Linha</th>
                        <th scope="col">Estado de carga</th>
                        <th scope="col" className="num">SOC</th>
                        <th scope="col" className="num">Previsão</th>
                        {!potenciaOculta && <th scope="col" className="num">Potência</th>}
                        <th scope="col">Leitura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {frota.telemetria.length ? (
                        frota.telemetria.slice(0, LIMITE_DA_TABELA).map((vehicle) => (
                          <LinhaDeFrotaCco
                            key={vehicle.prefixo}
                            veiculo={vehicle}
                            cfg={cfg}
                            mostraPotencia={!potenciaOculta}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={potenciaOculta ? 6 : 7} className="nx-cco-empty">
                            Nenhum ônibus visível para a fonte de telemetria ativa.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/*
                  O TRUNCAMENTO DECLARADO. `slice(0, 12)` na tela mais o
                  `LIMIT 200` no servidor: a tabela mostrava 12 linhas com o KPI
                  falando do total, sem nada dizendo que é um recorte.
                */}
                {/* As ressalvas entram como FRASES numa lista, e não
                    concatenadas com espaços literais: com o truncamento ausente
                    a concatenação deixava dois espaços no meio do parágrafo. */}
                <p className="nx-cco-footnote">
                  {[
                    fraseDeRecorteDaTabela(frota.telemetria.length, LIMITE_DA_TABELA),
                    truncamento,
                    "A previsão de término depende de três parcelas — estado de carga, potência de carga e capacidade cadastrada da bateria. A coluna diz qual delas falta, e não representa horário de saída confirmado.",
                  ]
                    .filter((t): t is string => t !== null)
                    .join(" ")}
                </p>
              </>
            );
          })()}
        </>}
      </section>

      <section className="nx-cco-workspace" aria-label="Pátio e exceções operacionais">
        <article className="nx-cco-panel nx-cco-panel--yard" aria-labelledby="cco-yard-title">
          <PanelHeader id="cco-yard-title" title="Pátio de recarga" subtitle="Disponibilidade dos conectores OCPP" href="/app/chargers" action="Abrir carregadores" />
          {isLoading || !data?.ocpp ? <Skeleton variant="row" count={5} /> : <>
            <div className="nx-cco-yard-summary" aria-label="Resumo do pátio">
              <span><StateDot tone="live" /> {counts.available} livres</span><span className="nx-cco-yard-summary__charging"><StateDot tone="muted" /> {counts.charging} carregando</span><span className="nx-cco-yard-summary__alert"><StateDot tone="warning" /> {counts.alert} em alerta</span><span className="nx-cco-yard-summary__offline">{counts.offline} sem sinal</span>
            </div>
            <PatioMatrix layout={data.ocpp.layoutPatio} />
          </>}
        </article>
        <div className="nx-cco-side-stack"><SessionPanel loading={isLoading} data={data?.ocpp} semResposta={!isLoading && data === undefined} /><AlertsPanel loading={isLoading} data={data?.ocpp} /></div>
      </section>

      <section className="nx-cco-lower-grid"><EnergyPanel loading={isLoading} data={data?.energia} /><UtilizationPanel loading={isLoading} data={data?.relatorios} /></section>
    </div>
  );
}

/**
 * Uma linha do quadrante de frota.
 *
 * TODA célula passa pelo classificador. O que havia:
 *  - `soc = Math.max(0, Math.min(100, vehicle.socPct))` — clamp sobre SOC, que
 *    esconde valor fora de faixa em vez de denunciá-lo, e a barra pintava 100%
 *    de verde para uma leitura de quatro semanas atrás;
 *  - `formatMinutes(null)` e `formatPower(null)` devolviam `"—"` para tudo:
 *    ausência, vencimento e impossibilidade com a mesma aparência, em duas
 *    colunas que com o Portal VGPL são estruturalmente vazias;
 *  - nenhuma coluna dizia a idade da leitura.
 */
function LinhaDeFrotaCco({
  veiculo,
  cfg,
  mostraPotencia,
}: {
  veiculo: CcoVeiculo;
  cfg: ConfiguracaoDeFrota;
  mostraPotencia: boolean;
}) {
  const leitura = comLeitura(veiculo);
  const estadoDaLeitura = estadoDeLeitura(leitura, cfg);
  const chrome = TRATAMENTO_DA_LEITURA[estadoDaLeitura];

  const estadoDoSoc = estadoDeValor(
    leitura,
    !fonteNaoFornece(veiculo.unsupportedFields, "soc_pct"),
    veiculo.socPct,
    cfg,
  );
  const socChrome = TRATAMENTO_DO_CAMPO[estadoDoSoc];
  const socFrase = FRASE_DO_CAMPO[estadoDoSoc]({
    grandeza: ROTULO_DO_CAMPO.soc_pct.nome,
    genero: ROTULO_DO_CAMPO.soc_pct.genero,
    fonte: veiculo.sourceLabel,
    idade: idadeFormatadaDaLeitura(leitura, fmtAge),
  });
  /* O SOC é apresentado como veio. Um valor fora de 0–100 é dado inconsistente e
     tem de aparecer: o clamp anterior o escondia, e barra a 100% sobre SOC de
     137% é a tela consertando o que devia denunciar. A LARGURA da barra é
     limitada — isso é geometria, não dado. */
  const soc = socChrome.mostraValor ? veiculo.socPct : null;
  const larguraDaBarra = soc === null ? 0 : Math.max(0, Math.min(100, soc));

  const causa = causaDoTempoRestante(
    {
      leitura,
      unsupportedFields: veiculo.unsupportedFields,
      potenciaKw: veiculo.potenciaKw,
      socPct: veiculo.socPct,
      tempoRestanteMin: veiculo.tempoRestanteMin,
    },
    cfg,
  );
  const fraseDaPrevisao = FRASE_DO_TEMPO_RESTANTE[causa](veiculo.sourceLabel);

  const estadoDaPotencia = estadoDeValor(
    leitura,
    !fonteNaoFornece(veiculo.unsupportedFields, "charging_power_kw"),
    veiculo.potenciaKw,
    cfg,
  );
  const potenciaChrome = TRATAMENTO_DO_CAMPO[estadoDaPotencia];

  return (
    <tr>
      <td className="mono nx-cco-table__id">{veiculo.prefixo}</td>
      <td>{veiculo.linha ?? <span className="nx-cco-muted">Sem linha atribuída</span>}</td>
      {/*
        A DEGRADACAO SAIU DO `<td>`, e o `<td>` era a forma que `FleetCells`
        abandonou. Aqui ela era pior que na tabela de frota: com `opacity: .55`
        no `<td>` inteiro o portador do SOC e uma BARRA — nao ha texto para
        carregar o dado — e a barra composta media 2,03:1 contra o proprio
        trilho, abaixo do limite de 3:1 de conteudo nao-textual (WCAG 1.4.11).
        A degradacao agora vai no PREENCHIMENTO da barra, por tinta. Ver
        `ESTILO_DEGRADADO_GRAFICO`.
      */}
      <td>
        {/*
          A CÉLULA USA `FRASE_DO_CAMPO`, e não um literal.

          Era `socChrome.sufixo ?? "Sem leitura"`. O sufixo é `null` em TRÊS
          estados — `sem-leitura`, `vencida-sem-grandeza` e `sem-grandeza` — e o
          literal era aplicado aos três. Medido ao vivo nas linhas 82010 e
          82011: esta coluna dizia "Sem leitura" enquanto a célula ao lado, na
          MESMA linha, dizia "Sem sinal há 672,0 h, e a última leitura não
          trouxe estado de carga". Duas afirmações incompatíveis a dois
          centímetros uma da outra.

          `resumoDoEstado` devolve a forma curta derivada do MESMO classificador
          que produz `socFrase`, então as duas não podem divergir.
        */}
        {soc === null ? (
          <span className="nx-cco-muted" title={socFrase}>
            {resumoDoEstado(estadoDoSoc)}
            <span className="nx-sr-only">{socFrase}</span>
          </span>
        ) : (
          /*
            O NOME ACESSIVEL DA BARRA — era `aria-label` num `role=generic`.

            ARIA 1.2 PROIBE nome acessivel em `role=generic`, e NVDA, JAWS e
            VoiceOver o IGNORAM: as 18 linhas tinham a barra de SOC muda, sem
            conteudo textual e sem papel. Nao e falta de solucao — o padrao
            certo esta duas telas ao lado (`<span className="nx-sr-only">`), e
            isto era duplicacao divergente.

            `role="img"` porque a barra E um grafico: com papel, o `aria-label`
            passa a ser valido. O `nx-sr-only` fica como cinto, porque leitor
            antigo que ignore o papel ainda le o texto.
          */
          <div className="nx-cco-soc" role="img" aria-label={socFrase}>
            <span
              aria-hidden="true"
              style={{
                width: `${larguraDaBarra}%`,
                ...(socChrome.degradado ? ESTILO_DEGRADADO_GRAFICO : {}),
              }}
            />
            {/* O `nx-sr-only` SAIU daqui: `role="img"` tem *Children
                Presentational: True*, entao o conteudo interno NAO e anunciado —
                ele era peso morto no DOM e ruido na contagem. O nome acessivel
                e o `aria-label`, que e valido porque o papel existe. */}
          </div>
        )}
      </td>
      {/*
        O NUMERO degrada; a RESSALVA e o texto para leitor de tela NAO. Era o
        `<td>` inteiro com `opacity: .55`, que arrastava o `(ultimo)` junto — o
        mesmo defeito que `CampoCelula` corrigiu na tabela de frota, aqui na
        segunda copia da regra.
      */}
      {/*
        O `title` SAIU do `<td>` — `socFrase` chegava TRÊS vezes à tecnologia
        assistiva por linha: o `aria-label` da barra (que é o nome acessível e
        deve ficar), o `nx-sr-only` da célula, e este `title` do `<td>` irmão.
        Com 18 linhas são ~54 repetições da mesma frase, e verbosidade em leitor
        de tela não é zelo: é o operador desistindo de ouvir até o fim.

        O `nx-sr-only` fica, porque é ele que associa a frase à CÉLULA do número.
      */}
      <td className="num mono">
        <span style={socChrome.degradado ? ESTILO_DEGRADADO_TEXTO : undefined}>
          {soc === null ? <span aria-hidden="true">—</span> : `${soc.toFixed(0)}%`}
        </span>
        {/* `marginLeft` resolvia o visual e não o anúncio: o leitor de tela lia
            "60%(último)" como uma palavra só. O espaço literal mora no
            componente, uma vez. */}
        {soc !== null && <SufixoDeRessalva sufixo={socChrome.sufixo} tamanho=".68em" />}
        <span className="nx-sr-only">{socFrase}</span>
      </td>
      <td className="num mono" title={fraseDaPrevisao}>
        {causa === "ok" && veiculo.tempoRestanteMin !== null ? (
          formatMinutes(veiculo.tempoRestanteMin)
        ) : (
          <span aria-hidden="true">—</span>
        )}
        <span className="nx-sr-only">{fraseDaPrevisao}</span>
      </td>
      {mostraPotencia && (
        <td className="num mono">
          <span style={potenciaChrome.degradado ? ESTILO_DEGRADADO_TEXTO : undefined}>
            {potenciaChrome.mostraValor && veiculo.potenciaKw !== null ? (
              `${veiculo.potenciaKw.toFixed(1)} kW`
            ) : (
              <span aria-hidden="true">—</span>
            )}
          </span>
        </td>
      )}
      {/*
        A COLUNA DE LEITURA, que antes não existia. Sem ela o quadrante
        apresentava snapshot de quatro semanas como estado atual — o BUG-SANITY
        "Veículo Offline com SoC = 100% em verde" do CLAUDE.md.
      */}
      {/* A FRASE VEM PRONTA do módulo — era montada aqui com três ternários e
          `fmtAge(veiculo.ageSeconds)`, o que obrigava a tela a LER o campo. Ler
          o campo é o que permite o alias que derrota a guarda de texto, e era
          também uma quarta redação da mesma frase. */}
      <td style={{ fontSize: ".78rem", color: "var(--nx-mute)" }}>
        {textoDaIdadeDaLeitura(leitura, cfg, fmtAge)}
        {chrome.rotulo !== null && (
          <span className="nx-sr-only"> ({chrome.rotulo})</span>
        )}
      </td>
    </tr>
  );
}

/**
 * A forma CURTA do estado, para célula estreita — derivada do mesmo
 * classificador que produz a frase longa.
 *
 * `Record` exaustivo e não um `??` com literal: o sufixo de
 * `TRATAMENTO_DO_CAMPO` é `null` em três estados diferentes, e qualquer
 * fallback único aplicado a ele afirma UM deles sobre os TRÊS. A frase longa
 * continua no `title` e no texto de leitor de tela; esta é só a etiqueta.
 */
const RESUMO_DO_ESTADO: Record<EstadoDoCampo, string> = {
  "nao-suportado": "Não fornecido",
  "inconsistencia-de-fonte": "Inconsistente",
  "sem-leitura": "Nunca reportou",
  futura: "Carimbo à frente",
  vencida: "Último valor",
  "vencida-sem-grandeza": "Sem sinal e sem o dado",
  "sem-grandeza": "Leitura sem o dado",
  corrente: "Em leitura",
};

function resumoDoEstado(estado: EstadoDoCampo): string {
  return RESUMO_DO_ESTADO[estado];
}

/*
  `semResposta` EXISTE PORQUE VAZIO E FALHA NAO SAO O MESMO ESTADO.

  Com o pedido recusado, `data` chega `undefined` e o painel escrevia "Nenhuma
  sessao com medicoes disponiveis para detalhar" — afirmacao sobre um conjunto
  que ninguem chegou a contar. E a mesma classe do vazio do Mapa sobre 403, e a
  mesma do esqueleto eterno: estado de FALHA apresentado como estado de DADO.
*/
function SessionPanel({
  loading,
  data,
  semResposta,
}: {
  loading: boolean;
  data: CcoOcpp | null | undefined;
  semResposta: boolean;
}) {
  const session = data?.sessaoSelecionada;
  return <article className="nx-cco-panel nx-cco-panel--session" aria-labelledby="cco-session-title">
    <PanelHeader id="cco-session-title" title="Sessão em foco" subtitle={session ? `${session.cpId} · conector ${session.connectorId}` : semResposta ? "Não foi possível consultar" : "Sem medições disponíveis"} />
    {loading ? <Skeleton variant="row" count={3} /> : semResposta ? <p className="nx-cco-empty">O portal não recebeu resposta para a consulta do pátio — o que aparece aqui depende dela.</p> : !session ? <p className="nx-cco-empty">Nenhuma sessão com medições disponíveis para detalhar.</p> : <dl className="nx-cco-session-grid">
      <div><dt>Energia medida</dt><dd>{session.kwhInjetado === null ? "—" : `${session.kwhInjetado.toFixed(1)} kWh`}</dd></div>
      <div><dt>Tensão estimada</dt><dd>{session.voltageV === null ? "—" : `${session.voltageV} V`}</dd></div>
      <div><dt>Corrente estimada</dt><dd>{session.currentA === null ? "—" : `${session.currentA} A`}</dd></div>
      <div><dt>Temperatura da bateria</dt><dd>{session.cellTempC === null ? "Sem leitura" : `${session.cellTempC.toFixed(1)} °C`}</dd></div>
    </dl>}
  </article>;
}

function AlertsPanel({ loading, data }: { loading: boolean; data: CcoOcpp | null | undefined }) {
  return <article className="nx-cco-panel nx-cco-panel--alerts" aria-labelledby="cco-alerts-title">
    <PanelHeader id="cco-alerts-title" title="Alertas e manutenção" subtitle="Ocorrências abertas mais recentes" href="/app/alerts" action="Ver alertas" />
    {loading ? <Skeleton variant="row" count={3} /> : !data?.alertas.length ? <p className="nx-cco-empty"><StateDot tone="live" /> Nenhum alerta ativo.</p> : <ul className="nx-cco-alert-list">
      {data.alertas.slice(0, 4).map((alert) => <li key={alert.id} className={`nx-cco-alert nx-cco-alert--${alert.severity}`}><span className="mono">{alert.cpId}</span><p>{alert.message}</p></li>)}
    </ul>}
  </article>;
}

function EnergyPanel({ loading, data }: { loading: boolean; data: CcoEnergia | null | undefined }) {
  const demo = data?.source === "demo";
  return <article className="nx-cco-panel nx-cco-panel--energy" aria-labelledby="cco-energy-title">
    <PanelHeader id="cco-energy-title" title="Energia e demanda" subtitle={demo ? "Fonte de medição pendente" : "Leitura da subestação"} href="/app/energia_demanda" action="Ver demanda" />
    {loading || !data ? <Skeleton variant="row" count={3} /> : <>
      {demo ? <p className="nx-cco-source-note"><IconTower /> Dados de demonstração. O medidor Kron ainda não está conectado.</p> : null}
      <div className="nx-cco-energy-values"><div><span>Demanda contratada</span><strong>{data.demandaContratadaKw} kW</strong></div><div><span>Janela de ponta</span><strong>{data.horarioPontaInicio}–{data.horarioPontaFim}</strong></div><div><span>Estratégia ativa</span><strong className="nx-cco-capitalize">{data.estrategiaAtual}</strong></div></div>
    </>}
  </article>;
}

function UtilizationPanel({ loading, data }: { loading: boolean; data: CcoRelatorios | null | undefined }) {
  return <article className="nx-cco-panel nx-cco-panel--utilization" aria-labelledby="cco-utilization-title">
    <PanelHeader id="cco-utilization-title" title="Uso dos carregadores" subtitle="Proxy operacional nas últimas 24 horas" href="/app/heatmap_utilizacao" action="Abrir heatmap" />
    {loading || !data ? <Skeleton variant="row" count={3} /> : <>
      <div className="nx-cco-utilization-head"><IconTrend /><div><span>Utilização estimada</span><strong>{data.eficienciaMediaPct == null ? '—' : `${data.eficienciaMediaPct.toFixed(0)}%`}</strong></div></div>
      <div className="nx-cco-utilization-list">{data.eficienciaPorCp.slice(0, 4).map((charger) => <div key={charger.cpId}><span className="mono">{charger.cpId}</span><span className="nx-cco-utilization-bar"><i style={{ width: `${Math.max(0, Math.min(100, charger.eficienciaPct ?? 0))}%` }} /></span><strong className="mono">{charger.eficienciaPct == null ? '—' : `${charger.eficienciaPct.toFixed(0)}%`}</strong></div>)}</div>
      <p className="nx-cco-footnote">Estimativa de energia por hora de sessão, usando potência nominal de referência. Não é eficiência elétrica certificada.</p>
    </>}
  </article>;
}
